import { Box } from '@mui/material';
import { btnSize, btnType } from '../../../../packages/ui/atoms/button';

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Header from '../../../../components/v2/Header';
import Typography from '../../../../components/v2/Typography';
import { typography } from '../../../../components/v2/Typography/index.constant';
import SubmitDrawer from '../caqh-information/components/drawers/SubmitDrawer';
import ImportDrawer from '../caqh-information/components/drawers/ImportDrawer';

import { useStyles } from './index.styles';
import PriorityEmpty from '../../member-detail/components/PriorityEmpty';
import { SectionNames } from '../caqh-information/index.constants';
import { getCaqhData, transformData } from '../common/utils.constants';
import { CardStatus, CardType, ProfileSectionCard } from '../common/card/profile-section-card';
import { InfoCard } from '../common/card/info-card';
import { putProviderData } from '../../../../services/datalab/datalab.service';
import { showSnackbar } from '../../../../redux/modules/snackbar';

const Degrees = ({ refresh, userId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showNewUpload, setShowNewUpload] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitList, setSubmitList] = useState({ filled: [], missing: [], list: [], title: undefined });
  const [degreesList, setDegreesList] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchCaqhData = async () => {
    const caqhData = await getCaqhData({ providerId: userId, type: 'education' });
    if (caqhData) {
      setDegreesList(caqhData);
    }
  };

  useEffect(() => {
    void fetchCaqhData();
  }, []);

  const onApprove = async id => {
    const type = 'education';
    const requestBody = {
      providerId: userId,
      data: {
        id,
        documentStatus: 'APPROVED',
      },
    };
    try {
      const response = await putProviderData(type, requestBody);
      if (response.status === 200) {
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Degree approved',
          }),
        );
        void fetchCaqhData();
      }
    } catch (err) {
      const msg = err.response?.data?.errors?.[0]?.endUserMessage || 'Something went wrong';
    }
  };

  const onUpdate = id => {
    const data = degreesList.find(i => i._id === id);
    const list = transformData(data?.mainData, CardType.EDUCATION);
    setShowSubmitModal(true);
    setSubmitList({
      filled: list?.filled || [],
      missing: list?.missing || [],
      list: list?.list || [],
      title: 'Degrees',
    });
    setSelectedRecord(data);
    setEditMode(true);
    setShowSubmitModal(true);
  };

  const onFormSubmit = async (data, edit) => {
    const type = 'education';
    let fileData = {};
    if (data.fileData) {
      fileData = data.fileData;
    }
    const requestBody = {
      providerId: userId,
      data: {
        id: selectedRecord._id,
        state: data.address.state,
        major: data.major,
        degree: data.degree,
        schoolStartDate: data.schoolStartDate,
        schoolEndDate: data.schoolEndDate,
        ...fileData,
      },
    };
    try {
      const { data: response } = await putProviderData(type, requestBody);
      if (response.success) {
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Degree data updated',
          }),
        );
      } else {
        setSubmitList({ filled: [], missing: [], list: [], title: undefined });
        void fetchCaqhData();
      }
    } catch (e) {
      console.warn(e);
      if (e.data) {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: e.data.error,
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: e.message || 'Something went wrong',
          }),
        );
      }
    }
    onCloseModal();
    void fetchCaqhData();
  };

  const resetInformation = () => {
    setSubmitList({ filled: [], missing: [], list: [], title: undefined });
  };

  const onCloseModal = () => {
    setSelectedRecord(null);
    setShowNewUpload(false);
    setShowSubmitModal(false);
    refresh();
    void fetchCaqhData();
    resetInformation();
  };

  return (
    <>
      <Header
        label="Degrees"
        actions={[
          {
            children: <Typography {...typography.body.normal.medium.bold}>Add new</Typography>,
            icon: 'plus',
            variant: btnType.PRIMARY,
            size: btnSize.SMALL,
            onClick: () => setShowNewUpload(true),
            className: classes.primaryBtn,
          },
        ]}
      />
      <InfoCard records={degreesList} />
      {degreesList?.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {degreesList?.map((degree, index) => {
            const cardData =
              degree?.documentStatus === CardStatus.DISCREPANCY ? degree?.filesData : degree?.mainData;
            const discrepancies = [];
            if (degree?.documentStatus === CardStatus.DISCREPANCY) {
              degree?.issues?.includes('degree') && discrepancies.push('name');
              degree?.issues?.includes('major') && discrepancies.push('major');
              degree?.issues?.includes('address.state') && discrepancies.push('states');
              degree?.issues?.includes('schoolStartDate') && discrepancies.push('issueDate');
              degree?.issues?.includes('schoolEndDate') && discrepancies.push('expiryDate');
            }
            return (
              <ProfileSectionCard
                key={index}
                data={degree}
                status={degree?.documentStatus || 'INCOMPLETE'}
                name={cardData?.degree || ''}
                major={cardData?.major || ''}
                states={[cardData?.address?.state || '']}
                issueDate={cardData?.schoolStartDate || ''}
                expiryDate={cardData?.schoolEndDate || ''}
                file={degree?.files?.[0] || 'Missing'}
                type={CardType.EDUCATION}
                onUpdate={() => onUpdate(degree?._id)}
                onApprove={() => onApprove(degree?._id)}
                discrepancies={discrepancies}
              />
            );
          })}
        </Box>
      ) : (
        <PriorityEmpty
          title="Degrees will appear here once added."
          description=""
          glyph="empty-degrees"
          glyphOrientation="portrait"
          isEmptyWithLottie
          lottieSize={128}
        />
      )}
      {showNewUpload && (
        <ImportDrawer
          open={showNewUpload}
          onClose={onCloseModal}
          type={SectionNames.EDUCATION}
          userId={userId}
        />
      )}
      {showSubmitModal && (
        <SubmitDrawer
          missedKeys={submitList.missing || []}
          filledKeys={submitList.filled}
          open={showSubmitModal}
          title="Degrees"
          subTitle="Degrees description"
          list={submitList.list}
          onClose={onCloseModal}
          userId={userId}
          onEdit={() => {
            setEditMode(true);
          }}
          isTabView
          editMode={editMode}
          onFormSubmit={onFormSubmit}
          selectedRecord={selectedRecord}
          fileType={SectionNames.EDUCATION}
        />
      )}
    </>
  );
};

export default Degrees;
